/**
 * Entitlements API endpoint (ADR 042)
 *
 * Per-workspace entitlement projection consumed by the UI as a RENDER HINT only.
 * The API enforcement middleware (`requireEntitlement`) is the real gate — this
 * map drives show / hide / upsell / degrade UX, never a security decision.
 *
 * Map semantics (load-bearing):
 *  - only catalogued + active features appear as keys;
 *  - a MISSING key = `granted` (un-gated) → render unchanged;
 *  - a present key carries its verdict (`granted | hidden | locked | soft`).
 */

import { http } from '@/lib/http'
import type { components } from '@xavior/contracts'

export type EntitlementVerdict = components['schemas']['EntitlementVerdict']
export type EntitlementMap = components['schemas']['EntitlementMap']

/**
 * GET /workspaces/:id/entitlements → the full {@link EntitlementMap}.
 *
 * Returns `{}` when the system is dormant (flag OFF / empty catalog) and a
 * fail-soft `{}` on a resolver error — both mean "nothing gated", i.e. render
 * everything. Member/owner read (mirrors `/agent-context`).
 */
export async function getWorkspaceEntitlements(
  authToken: string,
  workspaceId: string,
): Promise<EntitlementMap> {
  const response = await http.get<EntitlementMap>(
    `/workspaces/${workspaceId}/entitlements`,
    { authToken },
  )
  return response.data
}
